/**
 * @file GradeSelect 年级选择
 * @description 按教育阶段过滤年级选项；未传阶段时展示全部年级。
 *
 * @whenToUse 表单 / 筛选中的年级下拉，且需与教育阶段联动。
 * @whenNotToUse 与阶段无关的纯枚举下拉（直接用 Select + gradeMap）。
 */

import { useEffect } from 'react'
import { getGradeOptions } from '@/enum/common.ts'
import { Select } from '@/components/ui/select.tsx'

export type GradeSelectProps = {
  /** 当前年级值 */
  value?: string
  /** 值变化 */
  onChange?: (value: string) => void
  /**
   * 教育阶段；传入后仅展示该阶段年级。
   * 不传或空字符串时展示全部年级。
   */
  stage?: string
  /** 是否在选项前追加「空值」项（如筛选「全部」） */
  allowEmpty?: boolean
  /** 空值项文案；默认「全部」 */
  emptyLabel?: string
  /** 空值项的 value；默认 '' */
  emptyValue?: string
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
}

/**
 * 年级选择（可选按教育阶段过滤）
 */
export function GradeSelect({
  value,
  onChange,
  stage,
  allowEmpty = false,
  emptyLabel = '全部',
  emptyValue = '',
  placeholder = '请选择年级',
  disabled = false,
  invalid = false,
  className,
  id,
}: GradeSelectProps) {
  const gradeOptions = getGradeOptions(stage || undefined)
  const options = allowEmpty
    ? [{ value: emptyValue, label: emptyLabel }, ...gradeOptions]
    : gradeOptions

  useEffect(() => {
    if (value === undefined || value === emptyValue) return
    const valid = getGradeOptions(stage || undefined).some(
      (item) => item.value === value,
    )
    if (!valid) onChange?.(emptyValue)
  }, [stage, value, emptyValue, onChange])

  return (
    <Select
      id={id}
      value={value}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      invalid={invalid}
      className={className}
      onChange={onChange}
    />
  )
}
